import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { selectCurrentUser } from '../features/auth/authSlice';
import { useSwitchTenantMutation } from '../features/auth/authApi';
import { CreateWorkspaceModal } from './CreateWorkspaceModal';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface WorkspaceSwitcherProps {
    theme?: 'light' | 'dark';
    isCollapsed?: boolean;
}

export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({ theme = 'light', isCollapsed = false }) => {
    const { t } = useTranslation();
    const user = useAppSelector(selectCurrentUser);
    const dispatch = useAppDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [switchTenant, { isLoading }] = useSwitchTenantMutation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const availableTenants = user?.availableTenants || [];
    
    // Find active tenant info
    const activeTenant = availableTenants.find(tenant => tenant.id === user?.tenantId) || {
        id: user?.tenantId || '',
        name: t('workspaceSwitcher.activeWorkspace'),
        slug: ''
    };

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSwitch = async (slug: string) => {
        if (!slug || slug === activeTenant.slug || isLoading) return;
        
        try {
            setIsOpen(false);
            const response = await switchTenant({ tenant_slug: slug }).unwrap();
            toast.success(t('workspaceSwitcher.swappedToWorkspace', { name: response.user?.fullName || slug }));

            // The cache wipe (needed because endpoints like getPermissions/getRoles
            // have a tenant-independent cache key) now happens inside the
            // switchTenant mutation itself, right AFTER the new token is stored.
            // Doing it again here ran before that and refetched with the old
            // workspace's token - see authApi.switchTenant.

            const isEmployee = response.user?.role?.toLowerCase() === 'employee';
            navigate(isEmployee ? '/ess/dashboard' : '/dashboard');
        } catch (error: any) {
            // The backend's error is an object ({ code, message }), not a string -
            // handing it to toast rendered an object as a React child and crashed
            // the whole app to a blank screen.
            const apiError = error?.data?.error;
            const message = typeof apiError === 'string'
                ? apiError
                : apiError?.message || error?.data?.message;
            toast.error(message || t('workspaceSwitcher.failedToSwitch'));
        }
    };

    const isDark = theme === 'dark';

    // Tailwind Class Builders
    const containerClasses = `relative w-full ${isCollapsed ? 'flex justify-center' : 'px-4'}`;
    
    const buttonClasses = isCollapsed
        ? `h-10 w-10 rounded-xl flex items-center justify-center font-black text-sm uppercase transition-all duration-300 shadow-sm border ${
            isDark 
                ? 'bg-white/5 border-white/10 hover:border-indigo-500/50 text-indigo-400' 
                : 'bg-blue-50 border-blue-100 hover:border-blue-300 text-blue-600'
          }`
        : `w-full flex items-center justify-between p-3.5 rounded-2xl border transition-all duration-300 text-left rtl:text-right cursor-pointer outline-none focus:outline-none ${
            isDark
                ? 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10 text-white'
                : 'bg-gray-50/50 border-gray-100 hover:bg-gray-50 hover:border-gray-200 text-gray-900'
          }`;

    const dropdownClasses = `absolute z-50 left-4 right-4 mt-2 py-2 rounded-2xl shadow-xl border overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 ${
        isDark
            ? 'bg-slate-800 border-slate-700/60 text-slate-200'
            : 'bg-white border-gray-100 text-gray-800'
    }`;

    return (
        <div className={containerClasses} ref={dropdownRef}>
            <button 
                onClick={() => !isLoading && setIsOpen(!isOpen)} 
                disabled={isLoading}
                className={buttonClasses}
                title={isCollapsed ? activeTenant.name : ''}
            >
                {isCollapsed ? (
                    isLoading ? (
                        <svg className="animate-spin h-5 w-5 text-current" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : (
                        activeTenant.name.charAt(0)
                    )
                ) : (
                    <>
                        <div className="flex items-center gap-3 min-w-0">
                            <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-xs font-black uppercase flex-shrink-0 ${
                                isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-blue-50 text-blue-600'
                            }`}>
                                {activeTenant.name.charAt(0)}
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t('workspaceSwitcher.currentWorkspace')}</p>
                                <p className="text-sm font-bold truncate leading-tight mt-0.5">{activeTenant.name}</p>
                            </div>
                        </div>
                        <svg 
                            className={`w-4 h-4 text-slate-400 transition-transform duration-300 flex-shrink-0 ml-2 rtl:ml-0 rtl:mr-2 ${isOpen ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </>
                )}
            </button>

            {isOpen && !isCollapsed && (
                <div className={dropdownClasses}>
                    <div className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400/80">
                        {t('workspaceSwitcher.availableWorkspaces')}
                    </div>
                    
                    <div className="max-h-60 overflow-y-auto py-1">
                        {availableTenants.map((tenant) => (
                            <button
                                key={tenant.id}
                                onClick={() => handleSwitch(tenant.slug)}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left rtl:text-right text-sm transition-all duration-200 outline-none focus:outline-none ${
                                    tenant.id === activeTenant.id
                                        ? isDark ? 'bg-indigo-600/20 text-indigo-400 font-bold' : 'bg-blue-50 text-blue-600 font-bold'
                                        : isDark ? 'hover:bg-white/5 text-slate-300' : 'hover:bg-gray-50 text-gray-700'
                                }`}
                            >
                                <div className={`h-6 w-6 rounded flex items-center justify-center text-xs font-black uppercase ${
                                    tenant.id === activeTenant.id
                                        ? isDark ? 'bg-indigo-500/30 text-indigo-400' : 'bg-blue-100 text-blue-600'
                                        : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-400'
                                }`}>
                                    {tenant.name.charAt(0)}
                                </div>
                                <span className="truncate flex-1">{tenant.name}</span>
                                {tenant.id === activeTenant.id && (
                                    <svg className="w-4 h-4 text-current" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>

                    <div className={`mt-2 border-t pt-1 px-1.5 ${isDark ? 'border-slate-700/60' : 'border-gray-100'}`}>
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                setIsCreateModalOpen(true);
                            }}
                            className={`w-full flex items-center justify-center gap-2 py-2 px-4 rounded-xl text-xs font-bold transition-all duration-200 ${
                                isDark 
                                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm shadow-indigo-950/20' 
                                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm shadow-blue-100'
                            }`}
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                            </svg>
                            {t('workspaceSwitcher.createWorkspace')}
                        </button>
                    </div>
                </div>
            )}

            <CreateWorkspaceModal 
                isOpen={isCreateModalOpen} 
                onClose={() => setIsCreateModalOpen(false)} 
            />
        </div>
    );
};
